import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

import { EnumUserRole } from 'lib/graphql';
import { BaseModule } from 'lib/modules';

// Lazy-loaded page components
const EmployeeReportingDashboardPage = lazy(() =>
  import(
    /* webpackChunkName: "employee-reporting" */ 'features/reporting/pages/employee-reporting-dashboard/employee-reporting-dashboard-page'
  ).then((m) => ({ default: m.EmployeeReportingDashboardPage })),
);

const OwnerReportingDashboardPage = lazy(() =>
  import(
    /* webpackChunkName: "owner-reporting" */ 'features/reporting/pages/owner-reporting-dashboard/owner-reporting-dashboard-page'
  ).then((m) => ({ default: m.OwnerReportingDashboardPage })),
);

const ForecastingPage = lazy(() =>
  import(
    /* webpackChunkName: "forecasting" */ 'features/reporting/pages/forecasting/forecasting-page'
  ).then((m) => ({ default: m.ForecastingPage })),
);

const FinancialReportingConfigPage = lazy(
  () =>
    import(
      /* webpackChunkName: "financial-reporting-config" */ 'features/reporting/pages/financial-reporting-config/financial-reporting-config-page'
    ),
);

/**
 * Reporting Module Implementation
 */
class ReportingModuleClass extends BaseModule {
  id = 'REPORTING';
  name = 'Reporting';
  label = 'Reporting & Analytics';
  description = 'Processor reporting, analytics, and forecasting';
  version = '1.0.0';
  /**
   * Creates route objects for the Reporting module
   * @param ProtectedRoute
   * @param LazyWrapper
   * @param settings
   * @param ModuleProtectedRoute
   */
  createRoutes = (
    ProtectedRoute: any,
    LazyWrapper: any,
    settings?: any,
    ModuleProtectedRoute?: any,
  ): RouteObject[] => {
    const routes: RouteObject[] = [];
    const RouteProtector = ModuleProtectedRoute || ProtectedRoute;

    // Operations reporting (Vendor)
    routes.push({
      path: '/processor/reporting',
      element: (
        <RouteProtector
          component={EmployeeReportingDashboardPage}
          moduleId="REPORTING"
          allowedUserRoles={[EnumUserRole.Vendor]}
        />
      ),
    });

    // Revenue reporting (VendorOwner/Admin)
    routes.push({
      path: '/processor/owner',
      element: (
        <RouteProtector
          component={OwnerReportingDashboardPage}
          moduleId="REPORTING"
          allowedUserRoles={[EnumUserRole.VendorOwner, EnumUserRole.Admin]}
        />
      ),
    });

    // Forecasting
    routes.push({
      path: '/processor/forecasting',
      element: (
        <RouteProtector
          component={ForecastingPage}
          moduleId="REPORTING"
          allowedUserRoles={[EnumUserRole.VendorOwner, EnumUserRole.Admin]}
        />
      ),
    });

    // Reporting configuration
    routes.push({
      path: '/processor/reporting-config',
      element: (
        <RouteProtector
          component={FinancialReportingConfigPage}
          moduleId="REPORTING"
          allowedUserRoles={[EnumUserRole.VendorOwner, EnumUserRole.Admin]}
        />
      ),
    });

    return routes;
  };
}

// Export singleton instance
export const ReportingModule = new ReportingModuleClass();
